"use client";

import { CommentFeed } from "@/app/components/news/BlogComment";
import PriceChange from "@/app/components/PriceChange";
import SocialShare from "@/app/components/SocialShares";
import logoBlog from "@/asset/images/logo-blog-klarda.png";
import { BASE_URL, IMAGE_CDN, IMAGE_CDN_BLOG } from "@/constant/apiPath";
import { useAuth } from "@/context/AuthContext";
import { getPublishedTime, NewsFromProjectCategory, sanitizeHtmlContent } from "@/helpers";
import useAxiosQuery from "@/hooks/useMyQuery";
import useMyMutation from "@/hooks/userMyMutation";
import useTimeCounter from "@/hooks/useTimeCounter";
import type { BlogData, INewsResponse } from "@/models/news";
import type { IProject } from "@/models/project";
import { findHeadingByContent, replaceDivsWithTweets, scrollElementIntoView } from "@/utils/blogUtils";
import { CommentOutlined, ShareAltOutlined, UserOutlined } from "@ant-design/icons";
import { Avatar, Button, Dropdown, Form, Input, message, Tag, Typography } from "antd";
import { Skeleton } from "antd/lib";
import classNames from "classnames";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import imageBlog from "@/asset/images/image-blog.png";
import imageExample from "@/asset/images/image-example.png";
import { useRouter } from "next/navigation";
import { useLanguageContext } from "@/context/LanguageContext";
import { useShareModalContext } from "@/context/ShareContext";
import DonateModal from "../DonateModal";
import DonateIcon from "../icons/DonateIcon";
import { EnhanceTokenLinks } from "./EnhanceTokenLinksWrapper";
  
const { Title } = Typography;

const NewsMainContent: FC<{
  data?: BlogData & INewsResponse;
}> = ({ data }) => {
  const { language } = useLanguageContext();
  const t = useTranslations("news");
  const { user, openLoginModal } = useAuth();
  const [formCommentFeed] = Form.useForm();
  const [isCallMutateComment, setIsCallMutateComment] = useState<boolean>(false);
  const isKlarda = data?.is_klarda !== false;
  const [totalComment, setTotalComment] = useState(0);
  const { showModal } = useShareModalContext();
  const [isDonateModalOpen, setIsDonateModalOpen] = useState(false);
  const [guestComment, setGuestComment] = useState("");

  const { data: tokensRes } = useAxiosQuery<{ data?: IProject[] }>({
    url: `/v1/admin/token/token-by-list?symbol=${encodeURIComponent(
      isKlarda ? data?.tokens?.join(",") ?? "" : data?.token_symbols?.join(",") ?? "",
    )}`,
  });
  const { mutate: triggerPostComment } = useMyMutation({
    url: `/v1/blog/${data?.id}/comment`,
    method: "post",
  });

  const newsInfo = useMemo(
    () => ({
      tokenListInfo:
        tokensRes?.data?.filter(item =>
          isKlarda ? data?.tokens?.includes(item.symbol) : data?.token_symbols?.includes(item.symbol),
        ) ?? [],
      tags: (isKlarda ? data?.tags : data?.categories) || [],
      publishDate: isKlarda ? getPublishedTime(data) : data?.published_at || "",
      contentThumbnail: isKlarda
        ? `${IMAGE_CDN_BLOG}${data?.thumbnail?.imageKey}`
        : data?.thumbnail ||
          (data?.klarda_categories?.includes(NewsFromProjectCategory) ? imageBlog.src : imageExample.src),
    }),
    [data, tokensRes, isKlarda],
  );

  const isContentSameDescription = useMemo(
    () => sanitizeHtmlContent(data?.content || "") === sanitizeHtmlContent(data?.description || ""),
    [data?.content, data?.description],
  );

  useEffect(() => {
    const tableOfContents = document.getElementById("lexical-blog-detail");

    if (tableOfContents) {
      const clickHandler = (event: Event) => {
        const target = event.target as HTMLElement;
        if (target.closest("h1, h2, h3, h4, h5, h6")) {
          return;
        }

        if (target.closest('div[data-lexical-collapsible-content="true"]')) {
          const targetElement = findHeadingByContent((event.target as HTMLElement).textContent || "");
          if (targetElement) {
            scrollElementIntoView(targetElement, 100);
          }
        }
      };

      tableOfContents.addEventListener("click", clickHandler);

      return () => {
        tableOfContents.removeEventListener("click", clickHandler);
      };
    }
  }, []);

  useEffect(() => {
    if (data?.content) {
      replaceDivsWithTweets(data.content);

      // We've removed the problematic token enhancement code that was causing the error
      // The token enhancement is now handled by the EnhanceTokenLinks component
    }
  }, [data?.content]);

  const handleSubmitComment = useCallback(
    ({ comment }: { comment: string }) => {
      if (!comment.trim()) {
        return;
      }

      if (!user) {
        setGuestComment(comment);
        openLoginModal();
        return;
      }

      formCommentFeed.resetFields();
      setGuestComment("");
      setIsCallMutateComment(true);

      triggerPostComment({
        content: comment,
        post_id: data?.id,
      })
        .then(() => {
          setGuestComment("");
          formCommentFeed.resetFields();
          formCommentFeed.setFields([]);
          setIsCallMutateComment(true);
        })
        .catch((e: { response: { data: { message: any } } }) =>
          message.error(e?.response?.data?.message || "Failed to post comment!"),
        );
    },
    [data?.id, formCommentFeed, triggerPostComment, user, openLoginModal],
  );

  return (
    <div className="w-full" id="lexical-blog-detail">
      {/* Add our new token enhancement component right at the top level */}
      {data?.content && <EnhanceTokenLinks />}

      {data?.content || data?.description ? (
        <div className="flex flex-col space-y-4 w-full">
          <Typography.Title level={1} className="m-0">
            {data?.title}
          </Typography.Title>
          <BlogTags tags={newsInfo.tags} />

          <SocialShare
            url={isKlarda ? `${BASE_URL}/${language}/news/${data?.url}` : `${BASE_URL}/${language}/news/${data?.id}`}
            className="gap-4 ml-2 xl:fixed xl:left-3 xl:z-50 xl:flex-col xl:bg-white xl:border xl:border-gray-200 xl:rounded-lg xl:p-2"
            title="Check out Market news with klarda.com"
            description="Check out Market news with klarda.com"
          />

          {/* header */}
          <div className="flex flex-col justify-between space-y-1 border-y-[1px] border-gray-200 py-3 flex-wrap gap-3">
            <div className="flex justify-between items-center">
              <BlogAuthor
                img={
                  isKlarda
                    ? data.user_id?.profile_information?.avatar
                      ? `${IMAGE_CDN}${data.user_id?.profile_information?.avatar}`
                      : logoBlog.src
                    : data?.source_logo
                }
                name={
                  isKlarda
                    ? data?.user_id?.profile_information?.display_name || data?.creator || "Klarda Blog"
                    : data?.source_title
                }
                publishDate={newsInfo.publishDate}
              />
              <div className="flex gap-2 md:gap-4 flex-wrap justify-end md:justify-start">
                <TokenList tokenList={newsInfo.tokenListInfo} />

                <div className="gap-6 hidden sm:flex">
                  {/* total comment */}
                  {user && (
                    <div className="flex items-center gap-2 text-primary font-semibold">
                      <CommentOutlined />
                      <span>
                        {totalComment === 0
                          ? "No comments"
                          : totalComment === 1
                          ? `${totalComment} Comment`
                          : `${totalComment} Comments`}
                      </span>
                    </div>
                  )}
                </div>
                <Button
                  icon={<ShareAltOutlined />}
                  onClick={() =>
                    showModal({
                      featureName: `New ${data?.title}`,
                    })
                  }
                >
                  Share
                </Button>
                <Button
                  icon={<DonateIcon />}
                  className="border-[#046C4E] text-[#046C4E] bg-[#DEF7EC] hover:bg-[#046C4E] hover:text-white hover:border-[#046C4E] transition-colors"
                  onClick={() => setIsDonateModalOpen(true)}
                >
                  Donate
                </Button>
              </div>
            </div>
          </div>
          {/* end header */}

          {/* content */}
          {(isKlarda || (!isKlarda && !!data?.thumbnail)) && (
            <div className={!isKlarda ? "max-w-2xl m-auto" : ""}>
              <img
                src={newsInfo.contentThumbnail || ""}
                alt={data?.title || "Klarda Blog"}
                width={400}
                height={300}
                className="relative m-auto flex w-full object-contain"
              />
            </div>
          )}
          {/* <AdUnit
            slot="4479042342"
            style={{
              display: "block",
              textAlign: "center",
            }}
          /> */}
          {isKlarda ? (
            data?.content && <div className="max-w-full overflow-x-auto" id="blog-detail" />
          ) : (
            <>
              {!!data?.description?.trim() && !isContentSameDescription && (
                <Typography.Paragraph>
                  <blockquote className={"p-3"}>
                    <div dangerouslySetInnerHTML={{ __html: data?.description }} />
                  </blockquote>
                </Typography.Paragraph>
              )}
              <Typography.Paragraph>
                <div
                  dangerouslySetInnerHTML={{
                    __html:
                      sanitizeHtmlContent(data?.content || "").trim() || sanitizeHtmlContent(data?.description || ""),
                  }}
                />
              </Typography.Paragraph>

              <Link href={data?.link || ""} target="_blank" className="text-primary">
                {t("View_from_source")}
              </Link>
            </>
          )}
          {/* claimer */}
          <div className="text-sm text-gray-500 p-6 mb-6 pt-0">
            <span className="font-bold text-gray-700">Disclaimer: </span>
            The content provided in this article is for informational and educational purposes only and does not
            constitute financial, investment, or trading advice. Any actions you take based on the information provided
            are solely at your own risk. We are not responsible for any financial losses, damages, or consequences
            resulting from your use of this content. Always conduct your own research and consult a qualified financial
            advisor before making any investment decisions. <a href="https://docs.klarda.com/" target="_blank" className="underline">Read more</a>
          </div>
          {/* author */}


          {/* comment */}
          <div className="flex flex-col space-y-4 w-full">
            <Title level={4}>Leave a Reply</Title>
            <Form
              form={formCommentFeed}
              onFinish={handleSubmitComment}
              className="border border-gray-200 p-5 rounded-lg"
              initialValues={{ comment: guestComment }}
            >
              <Title level={5}>Your message</Title>

              <Form.Item className="m-0 flex-1" name="comment" id="comment-section">
                <Input.TextArea
                  rows={4}
                  className="bg-transparent text-sm placeholder:text-[#00000040]"
                  onChange={e => !user && setGuestComment(e.target.value)}
                />
              </Form.Item>
              <div className="mt-2 flex justify-between">
                <div className="flex items-center">
                  {user ? (
                    <>
                      {user?.profile_information?.avatar ? (
                        <Avatar src={`${IMAGE_CDN}${user?.profile_information?.avatar}`} size={32} />
                      ) : (
                        <Avatar icon={<UserOutlined />} size={32} />
                      )}
                      <Typography.Text className="ml-2 text-sm">{user?.username}</Typography.Text>
                    </>
                  ) : (
                    <></>
                  )}
                </div>

                <Button htmlType="submit" type="primary" className={classNames("flex items-center justify-center")}>
                  Post comment
                </Button>
              </div>
            </Form>

            {user && (
              <CommentFeed
                postId={data?.id || ""}
                likeCallback={() => null}
                isCallMutateComment={isCallMutateComment}
                setIsCallMutateComment={setIsCallMutateComment}
                isBeginComment={true}
                setTotalComment={setTotalComment}
              />
            )}
          </div>
        </div>
      ) : (
        <div className="w-full lg:w-[calc(min(100vw,1152px)-400px)]">
          <Skeleton active paragraph={{ rows: 20 }} />
        </div>
      )}
      {isDonateModalOpen && (
        <DonateModal isHideAddress open={isDonateModalOpen} onClose={() => setIsDonateModalOpen(false)} data={data} />
      )}
    </div>
  );
};

const BlogTags = ({ tags }: { tags: string[] }) => {
  const { language } = useLanguageContext();
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const visibleTags = 4;
  const remainingTags = tags.length - visibleTags;

  return (
    <div className="flex flex-wrap justify-start items-center">
      {tags.slice(0, visibleTags).map((tag, index) => (
        <Link href={`/${language}/news/tags/${tag}`} key={index} target="_blank">
          <Tag className="cursor-pointer bg-[#faf7f text-primary text-sm font-medium mr-2" bordered={false}>
            {tag}
          </Tag>
        </Link>
      ))}
      {remainingTags > 0 && (
        <Dropdown
          menu={{
            items: tags.slice(visibleTags).map((tag, index) => ({
              key: index,
              label: (
                // <Link href={`/${language}/news/tags/${tag}`} target="_blank">
                <>{tag}</>
                // </Link>
              ),
            })),
          }}
          open={isDropdownVisible}
          onOpenChange={setIsDropdownVisible}
        >
          <Tag
            className="cursor-pointer bg-[#faf7f text-primary text-sm font-medium mb-2"
            bordered={false}
            onClick={() => setIsDropdownVisible(!isDropdownVisible)}
          >
            +{remainingTags}
          </Tag>
        </Dropdown>
      )}
    </div>
  );
};

const BlogAuthor = ({ img, name, publishDate }: { img: string; name: string; publishDate: string }) => {
  const timeCounter = useTimeCounter({ publishDate: publishDate });
  return (
    <div className="flex items-center gap-2 whitespace-nowrap">
      {img ? <Avatar src={img} size={32} /> : <Avatar src={logoBlog.src} size={32} alt="Klarda Blog" />}
      <div className="flex gap-3">
        <Typography.Text strong ellipsis style={{ color: "#1A56DB" }}>
          {name}
        </Typography.Text>
        <Typography.Text type="secondary">{timeCounter}</Typography.Text>
      </div>
    </div>
  );
};

const TokenInfoItem = ({ token, isViewMore = false }: { token: IProject; isViewMore?: boolean }) => {
  const router = useRouter();
  const { language } = useLanguageContext();

  const handleClickToken = useCallback(() => {
    router.push(`/${language}/markets/${token.symbol}`);
  }, [language, router, token.symbol]);

  return (
    <div
      onClick={handleClickToken}
      className={classNames("cursor-pointer rounded-lg flex items-center space-x-2 p-1", {
        "bg-black bg-opacity-5": !isViewMore,
      })}
    >
      <div className="flex items-center">
        <Avatar src={token.logo_uri} alt={token.symbol} size={24} />
        <span className="ml-2 font-bold">{token.symbol}</span>
      </div>
      <PriceChange
        className="min-w-fit"
        price={token.last_24h_price > 0 ? (token.price - token.last_24h_price) / token.last_24h_price : 0}
      />
    </div>
  );
};

const TokenList = ({ tokenList }: { tokenList: IProject[] }) => (
  <div className="flex flex-wrap items-stretch gap-2">
    {tokenList?.slice(0, 2).map((item, index) => (
      <TokenInfoItem key={index} token={item} />
    ))}
    {tokenList?.length > 3 && (
      <div className="flex items-center h-full rounded-sm p-1 bg-black bg-opacity-5">
        <Dropdown
          menu={{
            items: tokenList?.slice(3)?.map(item => ({
              key: item.id,
              label: (
                <div className="w-full">
                  <TokenInfoItem token={item} isViewMore={true} />
                </div>
              ),
            })),
          }}
        >
          <span className="cursor-pointer px-2 text-primary font-bold">+{tokenList?.length - 3}</span>
        </Dropdown>
      </div>
    )}
  </div>
);

export default NewsMainContent;
